/**
 * filter-bar.js — Ruginza theme
 *
 * Strategy (updated):
 *  - Filter / sort / chip / price changes update the product grid IN PLACE
 *    via the Shopify Section Rendering API (?sections=...). No full reload.
 *  - Multi-select works: ticks are debounced into a single fetch.
 *  - After swapping the grid we re-reveal Impulse's [data-aos] items and nudge
 *    the lazy <image-element>s, so products are visible immediately (no scroll).
 *  - Count, active chips and the "All Filters" badge are synced from the
 *    re-rendered filter-bar section WITHOUT rebuilding the drawer.
 *  - history.pushState keeps filtered URLs shareable/bookmarkable; popstate
 *    re-renders. On any fetch error we fall back to a full navigation.
 */
(function () {
  'use strict';

  /* ── Apply CSS vars from Liquid settings ── */
  (function applySettings() {
    const s = window.__filterBarSettings;
    if (!s) return;
    const r = document.documentElement.style;
    const map = {
      '--fb-primary-bg':        s.primary_btn_bg,
      '--fb-primary-text':      s.primary_btn_text,
      '--fb-primary-bg-hover':  s.filter_btn_hover,
      '--fb-primary-radius':    s.btn_radius         != null ? s.btn_radius + 'px'        : null,
      '--fb-btn-bg':            s.filter_btn_bg,
      '--fb-btn-text':          s.filter_btn_text,
      '--fb-btn-border':        s.filter_btn_border,
      '--fb-btn-bg-hover':      s.filter_btn_hover,
      '--fb-btn-radius':        s.filter_btn_radius  != null ? s.filter_btn_radius + 'px' : null,
      '--fb-sort-bg':           s.sort_bg,
      '--fb-sort-text':         s.sort_text,
      '--fb-sort-border':       s.sort_border,
      '--fb-sort-radius':       s.sort_radius        != null ? s.sort_radius + 'px'       : null,
      '--fb-chip-bg':           s.active_chip_bg,
      '--fb-check-active':      s.check_color,
      '--fb-bar-bg':            s.bar_bg,
      '--fb-drawer-bg':         s.drawer_bg,
      '--fb-drawer-header-bg':  s.drawer_header_bg,
      '--fb-drawer-apply-bg':   s.drawer_apply_bg,
      '--fb-drawer-apply-text': s.drawer_apply_text,
    };
    Object.entries(map).forEach(([k, v]) => { if (v) r.setProperty(k, v); });
  })();

  /* ── Selectors ── */
  const S = {
    filterBar:          '#FilterBar',
    overlay:            '#FilterOverlay',
    drawer:             '#FilterDrawer',
    openDrawer:         '.js-open-drawer',
    closeDrawer:        '.js-close-drawer',
    accordionToggle:    '.js-accordion-toggle',
    dropdownWrap:       '.filter-dropdown',
    dropdownToggle:     '.js-dropdown-toggle',
    dropdownPanel:      '.filter-dropdown__panel',
    filterInputs:       '.js-filter-input',
    productCount:       '.js-product-count',
    productCountDrawer: '.js-product-count-drawer',
    sortToggle:         '.js-sort-toggle',
    sortPanel:          '#SortDropdown',
    sortOption:         '.js-sort-option',
    clearAll:           '.js-clear-all',
    activeChips:        '#ActiveFilterChips',
    filterChip:         '.js-filter-chip',
    pricePresets:       '.price-presets',
    pricePreset:        '.js-price-preset',
    /* AJAX targets */
    ajaxContent:        '#CollectionAjaxContent',
    gridWrap:           '#rfbGridWrap',
    leftGroup:          '.filter-bar__left',
    mobileCount:        '.filter-bar__mobile-count',
  };

  function debounce(fn, ms) {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }

  /* ════════════════════════════════════════
     MATCH THEME CONTAINER WIDTH
  ════════════════════════════════════════ */
  function matchThemeContainerWidth() {
    const filterInner = document.querySelector('.filter-bar__inner');
    const chipsInner  = document.querySelector('.filter-chips__inner');
    const mobilebar   = document.querySelector('.filter-bar__mobile-bar');
    if (!filterInner) return;

    const pageWidth = document.querySelector(
      '.page-width:not(.section-filter-bar .page-width)'
    );

    if (pageWidth) {
      const computed  = window.getComputedStyle(pageWidth);
      const padLeft   = computed.paddingLeft;
      const padRight  = computed.paddingRight;
      const maxWidth  = computed.maxWidth;

      filterInner.style.paddingLeft  = padLeft;
      filterInner.style.paddingRight = padRight;
      filterInner.style.maxWidth     = maxWidth !== 'none' ? maxWidth : '100%';

      if (chipsInner) {
        chipsInner.style.paddingLeft  = padLeft;
        chipsInner.style.paddingRight = padRight;
        chipsInner.style.maxWidth     = maxWidth !== 'none' ? maxWidth : '100%';
      }

      if (window.innerWidth <= 768 && mobilebar) {
        const mobilePad = Math.max(parseInt(padLeft) || 16, 16) + 'px';
        mobilebar.style.paddingLeft  = mobilePad;
        mobilebar.style.paddingRight = mobilePad;
      }
      return;
    }

    const refEl = document.querySelector(
      '.page-content, .page-width, .container, .wrapper'
    );
    if (!refEl) return;

    const rect     = refEl.getBoundingClientRect();
    const padLeft  = Math.round(rect.left);
    const padRight = Math.round(window.innerWidth - rect.right);
    const pad      = Math.max(padLeft, padRight, 0);

    if (pad > 0 && pad < 300) {
      filterInner.style.paddingLeft  = pad + 'px';
      filterInner.style.paddingRight = pad + 'px';
      if (chipsInner) {
        chipsInner.style.paddingLeft  = pad + 'px';
        chipsInner.style.paddingRight = pad + 'px';
      }
    }
  }

  /* ════════════════════════════════════════
     BUILD FILTER URL from checked inputs
  ════════════════════════════════════════ */
  function buildUrl() {
    const url = new URL(window.location.href);
    const p   = url.searchParams;

    /* Clear only list-facet params; preserve price (.price.) + sort_by so a
       checkbox change never drops an active price preset or the chosen sort. */
    [...p.keys()]
      .filter(k => k.startsWith('filter.') && k.indexOf('.price.') === -1)
      .forEach(k => p.delete(k));

    document.querySelectorAll(S.filterInputs).forEach(inp => {
      if (inp.checked && !inp.disabled) p.append(inp.name, inp.value);
    });

    p.delete('page');
    return url;
  }

  /* ════════════════════════════════════════
     AJAX — Shopify Section Rendering, in place
  ════════════════════════════════════════ */
  function getSectionId(selector, fallback) {
    const el  = document.querySelector(selector);
    const sec = el && el.closest ? el.closest('.shopify-section') : null;
    if (sec && sec.id && sec.id.indexOf('shopify-section-') === 0) {
      return sec.id.slice('shopify-section-'.length);
    }
    return fallback;
  }

  /* Re-reveal injected grid: Impulse hides [data-aos] until scroll adds
     .aos-animate; the lazy <image-element>s load on connect. */
  function revealGrid(scope) {
    if (!scope) return;
    const aosNodes = scope.querySelectorAll('[data-aos]');
    aosNodes.forEach(el => el.classList.add('aos-init', 'aos-animate'));
    if (scope.matches && scope.matches('[data-aos]')) scope.classList.add('aos-init', 'aos-animate');

    scope.querySelectorAll('img[loading="lazy"]').forEach(img => img.setAttribute('loading', 'eager'));

    /* let any IntersectionObserver-based reveal/lazyload pick up the new nodes */
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
    });
  }

  function swapGrid(html) {
    const doc     = new DOMParser().parseFromString(html, 'text/html');
    const newWrap = doc.querySelector(S.ajaxContent);
    const curWrap = document.querySelector(S.ajaxContent);
    if (newWrap && curWrap) {
      const imported = document.importNode(newWrap, true);
      curWrap.replaceWith(imported);           /* connects → upgrades <image-element> */
      revealGrid(imported);
      return true;
    }
    /* fallback: swap just the grid */
    const ng = doc.querySelector(S.gridWrap);
    const cg = document.querySelector(S.gridWrap);
    if (ng && cg) {
      const imp = document.importNode(ng, true);
      cg.replaceWith(imp);
      revealGrid(imp);
      return true;
    }
    return false;
  }

  /* Surgically sync count / chips / badges from the re-rendered bar section.
     Deliberately does NOT rebuild #FilterDrawer (keeps it open + intact). */
  function syncFromBar(html) {
    const bd = new DOMParser().parseFromString(html, 'text/html');

    /* desktop left group (count + inline chips) */
    const nLeft = bd.querySelector(S.leftGroup);
    const cLeft = document.querySelector(S.leftGroup);
    if (nLeft && cLeft) cLeft.innerHTML = nLeft.innerHTML;

    /* mobile count line */
    const nMob = bd.querySelector(S.mobileCount);
    const cMob = document.querySelector(S.mobileCount);
    if (nMob && cMob) cMob.innerHTML = nMob.innerHTML;

    /* mobile active-chips strip (#ActiveFilterChips) — presence aware */
    const nChips = bd.querySelector(S.activeChips);
    const cChips = document.querySelector(S.activeChips);
    const bar    = document.querySelector(S.filterBar);
    if (nChips && cChips)      cChips.replaceWith(document.importNode(nChips, true));
    else if (nChips && !cChips && bar) bar.appendChild(document.importNode(nChips, true));
    else if (!nChips && cChips)  cChips.remove();

    /* "All Filters" badge on each open-drawer button (add/update/remove) */
    const nBtns = bd.querySelectorAll(S.openDrawer);
    const cBtns = document.querySelectorAll(S.openDrawer);
    cBtns.forEach((btn, i) => {
      const src = nBtns[i] || nBtns[0];
      if (!src) return;
      const nb = src.querySelector('.filter-btn__badge');
      const cb = btn.querySelector('.filter-btn__badge');
      if (nb && cb) cb.textContent = nb.textContent;
      else if (nb && !cb) btn.appendChild(document.importNode(nb, true));
      else if (!nb && cb) cb.remove();
    });

    /* drawer footer "View Results (N)" count */
    const nDc = bd.querySelector(S.productCountDrawer);
    if (nDc) document.querySelectorAll(S.productCountDrawer)
      .forEach(el => { el.textContent = nDc.textContent.trim(); });

    /* drawer header "N active" badge (add / update / remove) */
    const nHb = bd.querySelector('.filter-drawer__count-badge');
    const cHb = document.querySelector('.filter-drawer__count-badge');
    if (nHb && cHb) cHb.textContent = nHb.textContent;
    else if (!nHb && cHb) cHb.remove();
    else if (nHb && !cHb) {
      const title = document.querySelector('.filter-drawer__title');
      if (title) title.insertAdjacentHTML('afterend', nHb.outerHTML);
    }

    /* drawer group-header active counts + group--active class.
       Surgical: match each group by its accordion aria-controls id and update
       ONLY the count span + active class — never touch aria-expanded / is-expanded,
       so the open drawer + accordion state stay exactly as the user left them. */
    const curDrawer = document.querySelector(S.drawer);
    if (curDrawer) {
      bd.querySelectorAll('.filter-drawer__group').forEach(ng => {
        const nHdr = ng.querySelector('.js-accordion-toggle');
        const ctrl = nHdr && nHdr.getAttribute('aria-controls');
        if (!ctrl) return;
        const cHdr = curDrawer.querySelector('.js-accordion-toggle[aria-controls="' + ctrl + '"]');
        if (!cHdr) return;
        const cGroup = cHdr.closest('.filter-drawer__group');
        if (cGroup) cGroup.classList.toggle('filter-drawer__group--active',
          ng.classList.contains('filter-drawer__group--active'));
        const cLabel = cHdr.querySelector('.filter-drawer__group-label');
        if (!cLabel) return;
        const nCount = ng.querySelector('.filter-drawer__group-count');
        let   cCount = cLabel.querySelector('.filter-drawer__group-count');
        if (nCount && cCount) cCount.textContent = nCount.textContent;
        else if (nCount && !cCount) cLabel.insertAdjacentHTML('beforeend', nCount.outerHTML);
        else if (!nCount && cCount) cCount.remove();
      });
    }
  }

  let currentAbort = null;

  function applyFilters(targetUrl, opts) {
    opts = opts || {};
    const url = targetUrl
      ? (targetUrl instanceof URL ? targetUrl : new URL(targetUrl, window.location.origin))
      : buildUrl();

    const gridId = getSectionId(S.ajaxContent, 'main-collection');
    const barId  = getSectionId(S.filterBar, null);

    const fetchUrl = new URL(url.toString());
    const ids = [gridId];
    if (barId) ids.push(barId);
    fetchUrl.searchParams.set('sections', ids.join(','));

    showGridLoading();

    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();

    fetch(fetchUrl.toString(), {
      signal: currentAbort.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(data => {
        const gridHtml = data[gridId];
        if (gridHtml && !swapGrid(gridHtml)) throw new Error('grid not found in response');
        if (barId && data[barId]) syncFromBar(data[barId]);

        if (opts.push !== false) history.pushState({ rzFilter: true }, '', url.toString());

        /* keep drawer inputs + sort UI in sync (chip removal / clear / sort / back-fwd) */
        restoreStateFromUrl();

        hideGridLoading();
        currentAbort = null;
      })
      .catch(err => {
        if (err && err.name === 'AbortError') return;   /* superseded by a newer fetch */
        /* graceful fallback so filtering never silently fails */
        window.location.href = url.toString();
      });
  }

  const debouncedApply = debounce(() => applyFilters(), 350);

  function showGridLoading() {
    const g = document.querySelector(S.gridWrap);
    if (g) {
      g.style.transition    = 'opacity 0.2s ease';
      g.style.opacity       = '0.55';
      g.style.pointerEvents = 'none';
    }
    document.querySelector(S.filterBar)?.classList.add('filter-bar--loading');
  }

  function hideGridLoading() {
    const g = document.querySelector(S.gridWrap);
    if (g) { g.style.opacity = ''; g.style.pointerEvents = ''; }
    document.querySelector(S.filterBar)?.classList.remove('filter-bar--loading');
  }

  /* ════════════════════════════════════════
     DROPDOWNS
  ════════════════════════════════════════ */
  function initDropdowns() {
    document.querySelectorAll(S.dropdownToggle).forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const wrap    = btn.closest(S.dropdownWrap);
        const panel   = wrap.querySelector(S.dropdownPanel);
        const wasOpen = panel.classList.contains('is-open');
        closeAllDropdowns();
        closeSortPanel();
        if (!wasOpen) {
          panel.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', e => {
      if (!e.target.closest(S.dropdownWrap) &&
          !e.target.closest('.sort-dropdown')) {
        closeAllDropdowns();
        closeSortPanel();
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        closeAllDropdowns();
        closeSortPanel();
      }
    });
  }

  function closeAllDropdowns() {
    document.querySelectorAll(S.dropdownPanel)
      .forEach(p => p.classList.remove('is-open'));
    document.querySelectorAll(S.dropdownToggle)
      .forEach(b => b.setAttribute('aria-expanded', 'false'));
  }

  /* ════════════════════════════════════════
     SORT DROPDOWN
  ════════════════════════════════════════ */
  function initSort() {
    document.querySelectorAll(S.sortToggle).forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const panel   = btn.closest('.sort-dropdown')?.querySelector(S.sortPanel);
        if (!panel) return;
        const wasOpen = panel.classList.contains('is-open');
        closeAllDropdowns();
        closeSortPanel();
        if (!wasOpen) {
          panel.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.querySelectorAll(S.sortOption).forEach(opt => {
      const select = () => {
        const val = opt.dataset.value;
        if (!val) return;

        document.querySelectorAll(S.sortOption).forEach(o => {
          o.classList.remove('sort-dropdown__option--active');
          o.setAttribute('aria-selected', 'false');
          o.querySelector('.sort-dropdown__check')?.remove();
        });
        opt.classList.add('sort-dropdown__option--active');
        opt.setAttribute('aria-selected', 'true');
        opt.insertAdjacentHTML('beforeend',
          '<svg class="sort-dropdown__check" width="11" height="9" viewBox="0 0 11 9" fill="none">' +
          '<path d="M1 4.5L4 7.5L10 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>'
        );
        document.querySelectorAll('.sort-dropdown__current').forEach(s => {
          s.textContent = opt.textContent.replace(/[✓✔]/g, '').trim();
        });
        closeSortPanel();

        /* Update sort in place */
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', val);
        url.searchParams.delete('page');
        applyFilters(url);
      };

      opt.addEventListener('click', select);
      opt.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); }
      });
    });
  }

  function closeSortPanel() {
    document.querySelectorAll(S.sortPanel)
      .forEach(p => p.classList.remove('is-open'));
    document.querySelectorAll(S.sortToggle)
      .forEach(b => b.setAttribute('aria-expanded', 'false'));
  }

  /* ════════════════════════════════════════
     DRAWER
  ════════════════════════════════════════ */
  let lastFocused = null;

  function initDrawer() {
    const drawer  = document.querySelector(S.drawer);
    const overlay = document.querySelector(S.overlay);
    if (!drawer) return;

    document.querySelectorAll(S.openDrawer).forEach(btn => {
      btn.addEventListener('click', () => {
        lastFocused = btn;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        if (overlay) {
          overlay.style.display = 'block';
          requestAnimationFrame(() => overlay.style.opacity = '1');
        }
        document.body.style.overflow = 'hidden';
        document.querySelectorAll(S.openDrawer)
          .forEach(b => b.setAttribute('aria-expanded', 'true'));
        setTimeout(() => drawer.querySelector('button, input, a')?.focus(), 100);
      });
    });

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => { overlay.style.display = 'none'; }, 300);
      }
      document.body.style.overflow = '';
      document.querySelectorAll(S.openDrawer)
        .forEach(b => b.setAttribute('aria-expanded', 'false'));
      lastFocused?.focus();
    }

    document.querySelectorAll(S.closeDrawer).forEach(btn => {
      btn.addEventListener('click', () => {
        /* "View Results" applies the current selection immediately, then closes */
        if (btn.classList.contains('js-apply-filters')) {
          closeDrawer();
          applyFilters();
        } else {
          closeDrawer();
        }
      });
    });

    overlay?.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open'))
        closeDrawer();
    });

    drawer.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll(
        'button:not([disabled]),input:not([disabled]),a[href],[tabindex="0"]'
      )].filter(el => el.offsetParent !== null);
      if (!focusable.length) return;
      if (e.shiftKey && document.activeElement === focusable[0]) {
        e.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        e.preventDefault();
        focusable[0].focus();
      }
    });
  }

  /* ════════════════════════════════════════
     ACCORDION (drawer groups)
  ════════════════════════════════════════ */
  function initAccordion() {
    document.querySelectorAll(S.accordionToggle).forEach(btn => {
      const body = document.getElementById(btn.getAttribute('aria-controls'));
      if (btn.getAttribute('aria-expanded') === 'true' && body)
        body.classList.add('is-expanded');

      btn.addEventListener('click', () => {
        const b    = document.getElementById(btn.getAttribute('aria-controls'));
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !open);
        b?.classList.toggle('is-expanded', !open);
      });
    });
  }

  /* ════════════════════════════════════════
     FILTER INPUTS (checkboxes + swatches)
     Change → debounced in-place update (multi-select friendly)
  ════════════════════════════════════════ */
  function initFilterInputs() {
    document.querySelectorAll(S.filterInputs).forEach(inp => {
      inp.addEventListener('change', () => {
        const lbl = inp.closest('label');
        if (lbl) {
          lbl.classList.toggle('filter-check--active',  inp.checked);
          lbl.classList.toggle('filter-swatch--active', inp.checked);
        }
        debouncedApply();
      });
    });
  }

  /* ════════════════════════════════════════
     ACTIVE FILTER CHIPS + CLEAR ALL (delegated,
     survives grid/chip re-renders)
  ════════════════════════════════════════ */
  let chipsBound = false;
  function initChipsDelegation() {
    if (chipsBound) return;
    chipsBound = true;
    document.addEventListener('click', e => {
      const chip = e.target.closest ? e.target.closest(S.filterChip) : null;
      if (chip && chip.getAttribute('href')) {
        e.preventDefault();
        applyFilters(new URL(chip.getAttribute('href'), window.location.origin));
        return;
      }
      const clear = e.target.closest ? e.target.closest(S.clearAll) : null;
      if (clear && clear.getAttribute('href')) {
        e.preventDefault();
        applyFilters(new URL(clear.getAttribute('href'), window.location.origin));
        return;
      }
      const preset = e.target.closest ? e.target.closest(S.pricePreset) : null;
      if (preset) {
        e.preventDefault();
        handlePresetClick(preset);
      }
    });
  }

  /* ════════════════════════════════════════
     PRICE PRESETS (delegated) — tappable buckets.
     Values are in yen (JPY zero-decimal): never divide.
     Toggling routes gte/lte through the same AJAX apply path.
  ════════════════════════════════════════ */
  function handlePresetClick(preset) {
    const wrap    = preset.closest(S.pricePresets);
    if (!wrap) return;
    const gteName = wrap.dataset.gteName || 'filter.v.price.gte';
    const lteName = wrap.dataset.lteName || 'filter.v.price.lte';
    const gte     = preset.dataset.gte || '';
    const lte     = preset.dataset.lte || '';
    const isActive = preset.classList.contains('is-active');

    const url = new URL(window.location.href);
    const p   = url.searchParams;
    p.delete(gteName);
    p.delete(lteName);
    p.delete('page');
    if (!isActive) {                       /* tapping the active one clears price */
      if (gte) p.set(gteName, gte);
      if (lte) p.set(lteName, lte);
    }

    /* immediate UI feedback (authoritative sync happens after fetch) */
    wrap.querySelectorAll(S.pricePreset).forEach(b => {
      b.classList.remove('is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    if (!isActive) {
      preset.classList.add('is-active');
      preset.setAttribute('aria-pressed', 'true');
    }

    applyFilters(url);
  }

  function syncPricePresets(params) {
    document.querySelectorAll(S.pricePresets).forEach(wrap => {
      const gteName = wrap.dataset.gteName;
      const lteName = wrap.dataset.lteName;
      const curGte  = params.get(gteName) || '';
      const curLte  = params.get(lteName) || '';
      wrap.querySelectorAll(S.pricePreset).forEach(b => {
        const match = (b.dataset.gte || '') === curGte && (b.dataset.lte || '') === curLte;
        b.classList.toggle('is-active', match);
        b.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    });
  }

  /* ════════════════════════════════════════
     STICKY BAR (desktop)
  ════════════════════════════════════════ */
  function initSticky() {
    const section = document.querySelector('.section-filter-bar');
    const bar     = document.querySelector('.filter-bar__desktop');
    if (!section || !bar || section.dataset.sticky !== 'true') return;

    const sentinel = document.createElement('div');
    sentinel.style.cssText =
      'height:1px;pointer-events:none;position:absolute;top:0;left:0;width:100%;';
    section.prepend(sentinel);

    new IntersectionObserver(
      ([e]) => bar.classList.toggle('is-stuck', !e.isIntersecting),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    ).observe(sentinel);
  }

  /* ════════════════════════════════════════
     RESTORE STATE (from URL): sync inputs + sort UI
  ════════════════════════════════════════ */
  function restoreStateFromUrl() {
    const params = new URLSearchParams(window.location.search);

    document.querySelectorAll(S.filterInputs).forEach(inp => {
      const values = params.getAll(inp.name);
      const should = values.includes(inp.value);
      inp.checked  = should;

      const lbl = inp.closest('label');
      if (lbl) {
        lbl.classList.toggle('filter-check--active',  should);
        lbl.classList.toggle('filter-swatch--active', should);
      }
    });

    const sortBy = params.get('sort_by') || 'manual';
    document.querySelectorAll(S.sortOption).forEach(opt => {
      const active = opt.dataset.value === sortBy;
      opt.classList.toggle('sort-dropdown__option--active', active);
      opt.setAttribute('aria-selected', active ? 'true' : 'false');
      opt.querySelector('.sort-dropdown__check')?.remove();
      if (active) {
        opt.insertAdjacentHTML('beforeend',
          '<svg class="sort-dropdown__check" width="11" height="9" viewBox="0 0 11 9" fill="none">' +
          '<path d="M1 4.5L4 7.5L10 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>'
        );
        document.querySelectorAll('.sort-dropdown__current').forEach(s => {
          s.textContent = opt.textContent.replace(/[✓✔]/g, '').trim();
        });
      }
    });

    syncPricePresets(params);
  }

  /* ════════════════════════════════════════
     GLOBAL (bind once): chip delegation + popstate
  ════════════════════════════════════════ */
  let globalBound = false;
  function initGlobal() {
    if (globalBound) return;
    globalBound = true;
    initChipsDelegation();
    window.addEventListener('popstate', () => {
      applyFilters(new URL(window.location.href), { push: false });
    });
  }

  /* ════════════════════════════════════════
     BOOT
  ════════════════════════════════════════ */
  function init() {
    restoreStateFromUrl();
    initDropdowns();
    initSort();
    initDrawer();
    initAccordion();
    initFilterInputs();
    initSticky();
    initGlobal();
    setTimeout(matchThemeContainerWidth, 50);
  }

  window.addEventListener('resize', debounce(matchThemeContainerWidth, 100));

  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', init);
  else
    init();

  document.addEventListener('shopify:section:load', init);

})();
